import { UserType } from "@/hooks/useUrlParams";
import { IPayment } from "@/types/payment.types";
import { DATE_FORMAT_US_SLASHES } from "@/utils/dates";
import { Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { InformationFilled } from "@carbon/icons-react";
import BigNumber from "bignumber.js";
import dayjs from "dayjs";

function formatCareDates(
  periodStartDate: string,
  periodEndDate: string,
): string {
  const start = dayjs(periodStartDate);
  const end = dayjs(periodEndDate);

  if (!start.isValid() || !end.isValid()) {
    return "—";
  }

  if (start.month() !== end.month() || start.year() !== end.year()) {
    return `${start.format("MMM DD, YYYY")} - ${end.format("MMM DD, YYYY")}`;
  }

  return `${start.format("MMM DD")} - ${end.format("DD, YYYY")}`;
}

function formatReleasedAmount(amount: string): string {
  const value = new BigNumber(amount);
  if (!value.isFinite()) {
    return amount || "—";
  }

  return `$${value.toFormat(2)}`;
}

function billingPeriodTime(payment: IPayment): number {
  if (!payment.periodStartDate) {
    return Number.NEGATIVE_INFINITY;
  }

  const time = +new Date(payment.periodStartDate);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

export const getPaymentsTableColumns = ({
  userType,
}: {
  userType: UserType;
}): ColumnsType<IPayment> => [
  {
    title: "Name",
    dataIndex: "childName",
    key: "childName",
    sorter: (a, b) => (a.childName || "").localeCompare(b.childName || ""),
  },
  {
    title: "Child ID",
    dataIndex: "childId",
    key: "childId",
    sorter: (a, b) => (a.childId || 0) - (b.childId || 0),
  },
  ...(userType === UserType.PROVIDER
    ? [
        {
          title: "Household ID",
          dataIndex: "householdId",
          key: "householdId",
          sorter: (a: IPayment, b: IPayment) =>
            (a.householdId || 0) - (b.householdId || 0),
        },
      ]
    : [
        {
          title: "Provider ID",
          dataIndex: "providerId",
          key: "providerId",
          sorter: (a: IPayment, b: IPayment) =>
            (a.providerId || 0) - (b.providerId || 0),
        },
      ]),
  {
    title: "Certificate",
    dataIndex: "certificateId",
    key: "certificateId",
    render: (certificateId: IPayment["certificateId"]) => certificateId ?? "–",
    sorter: (a, b) => (a.certificateId || 0) - (b.certificateId || 0),
  },
  {
    title: "Care Dates",
    key: "careDates",
    render: (_: unknown, payment: IPayment) => {
      if (!payment.periodStartDate || !payment.periodEndDate) {
        return "—";
      }

      return formatCareDates(payment.periodStartDate, payment.periodEndDate);
    },
    sorter: (a, b) => {
      const periodDiff = billingPeriodTime(a) - billingPeriodTime(b);
      if (periodDiff !== 0) {
        return periodDiff;
      }

      const releaseDiff =
        +new Date(a.paymentReleaseDate || "") -
        +new Date(b.paymentReleaseDate || "");
      if (releaseDiff !== 0) {
        return releaseDiff;
      }

      return (a.transactionDetailId || 0) - (b.transactionDetailId || 0);
    },
    defaultSortOrder: "descend",
  },
  {
    title: "Program",
    dataIndex: "programDescription",
    key: "program",
    render: (description: string, payment: IPayment) =>
      description || payment.program,
    sorter: (a, b) =>
      (a.programDescription || a.program || "").localeCompare(
        b.programDescription || b.program || "",
      ),
  },
  {
    title: "Payment Type",
    dataIndex: "paymentTypeDescription",
    key: "paymentType",
    render: (description: string, payment: IPayment) =>
      description || payment.paymentTypeCode,
    sorter: (a, b) =>
      (a.paymentTypeDescription || a.paymentTypeCode || "").localeCompare(
        b.paymentTypeDescription || b.paymentTypeCode || "",
      ),
  },
  {
    title: "Released Amount",
    dataIndex: "releasedAmount",
    key: "releasedAmount",
    render: (amount: string, payment: IPayment) => {
      const note = payment.transactionNotes?.trim();

      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          {formatReleasedAmount(amount)}
          {note ? (
            <Tooltip
              title={<span className="whitespace-pre-wrap">{note}</span>}
            >
              <InformationFilled
                className="text-primary cursor-pointer"
                size={16}
              />
            </Tooltip>
          ) : null}
        </span>
      );
    },
    sorter: (a, b) => {
      const aAmount = new BigNumber(a.releasedAmount);
      const bAmount = new BigNumber(b.releasedAmount);
      return aAmount.minus(bAmount).toNumber();
    },
  },
  {
    title: "Payment Release Date",
    dataIndex: "paymentReleaseDate",
    key: "paymentReleaseDate",
    render: (date: string) => {
      const parsed = dayjs(date);
      return parsed.isValid() ? parsed.format(DATE_FORMAT_US_SLASHES) : "–";
    },
    sorter: (a, b) =>
      +new Date(a.paymentReleaseDate || "") -
      +new Date(b.paymentReleaseDate || ""),
  },
];
